import pluralize from 'pluralize'

import { TimelineStep, type TimelineStepState } from '@/components/ui/timeline'
import { formatDate, formatDateTimeShort } from '@/lib/formatters'
import { type SequenceStep, SequenceStepStatus, SequenceStepType } from '@/models/sequence'
import { SequenceStepContent } from './SequenceStepContent'

function title(step: SequenceStep): string {
  const done = step.status === SequenceStepStatus.COMPLETED
  switch (step.stepType) {
    case SequenceStepType.SEND_EMAIL:
      return done ? 'Email sent' : 'Send email'
    case SequenceStepType.SEND_SMS:
      return done ? 'SMS sent' : 'Send SMS'
    case SequenceStepType.CALL:
      return done ? 'Call reminder created' : 'Call reminder'
    case SequenceStepType.TEXT:
      return done ? 'Text reminder created' : 'Text reminder'
    case SequenceStepType.WAIT:
      return `${done ? 'Waited' : 'Wait'} ${pluralize('day', step.config.days ?? 0, true)}`
  }
}

const DESCRIPTION: Partial<Record<SequenceStepType, string>> = {
  [SequenceStepType.CALL]: 'Reminds the rep to call the lead.',
  [SequenceStepType.TEXT]: 'Reminds the rep to text the lead.',
}

interface SequenceTimelineStepProps {
  step: SequenceStep
  /** The step the sequence is waiting on — the first pending one while it runs or is paused. */
  current: boolean
  paused: boolean
}

export function SequenceTimelineStep({ step, current, paused }: SequenceTimelineStepProps) {
  const due = step.scheduledFor ? formatDate(step.scheduledFor) : null
  let state: TimelineStepState = 'pending'
  let meta: string | undefined = due ?? undefined
  if (step.status === SequenceStepStatus.COMPLETED) {
    state = 'done'
    meta = formatDateTimeShort(step.completedAt)
  } else if (step.status === SequenceStepStatus.SKIPPED) {
    state = 'done'
    meta = 'Skipped'
  } else if (current) {
    state = 'current'
    meta = paused ? 'Paused' : due ? `Due ${due}` : 'Up next'
  }

  return (
    <TimelineStep
      state={state}
      tone={step.status === SequenceStepStatus.SKIPPED ? 'neutral' : 'success'}
      title={title(step)}
      meta={meta}
      description={DESCRIPTION[step.stepType]}
    >
      <SequenceStepContent step={step} />
    </TimelineStep>
  )
}
